/**
 * Credit Purchase Modal
 *
 * Shown when users don't have enough credits to perform an action.
 * Uses the "Unlock" pattern with clear value propositions.
 *
 * Features:
 * - Shows current balance and required credits
 * - Credit pack options with bonus highlights
 * - Subscription upsell with ROI calculation
 * - Deep link to web checkout (iOS/Android can't use Stripe directly)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Zap,
  CreditCard,
  Sparkles,
  ArrowRight,
  Check,
  Crown,
  Gift,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { Platform, openPurchasePage } from '../../utils/platform';
import { useAuth } from '../../contexts/AuthContext';
import {
  usePaymentsEnabled,
  wasBlockedByKillSwitch,
  PAYMENTS_UNAVAILABLE_MESSAGE,
} from '../../hooks/usePaymentsEnabled';

const API_BASE = import.meta.env.VITE_API_URL || 'https://claude-essay-agent-production.up.railway.app';

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  bonusCredits: number;
  price: number;
  badge?: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  credits: number;
  price: number;
  billingPeriod: 'monthly' | 'yearly';
  monthlyEquivalent?: number;
  features: string[];
  badge?: string;
}

// Credit packs - one-time purchase, expire after 2 years
// Subscriptions offer better per-credit value
const CREDIT_PACKS: CreditPack[] = [
  { id: 'credits_10', name: 'Starter', credits: 10, bonusCredits: 0, price: 35 },
  { id: 'credits_25', name: 'Growth', credits: 25, bonusCredits: 2, price: 69 },
  { id: 'credits_50', name: 'Pro', credits: 50, bonusCredits: 5, price: 129, badge: 'POPULAR' },
  { id: 'credits_100', name: 'Agency', credits: 100, bonusCredits: 15, price: 239, badge: 'BEST VALUE' },
];

// Show yearly plans in modal (best value with 20% discount)
const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'starter_yearly',
    name: 'Starter',
    credits: 10,
    price: 278.40,
    billingPeriod: 'yearly',
    monthlyEquivalent: 23.20,
    features: [
      '10 credits/month',
      'BlogCraft AI Writer',
      'Basic Templates',
      'Email Support',
    ],
  },
  {
    id: 'pro_yearly',
    name: 'Pro',
    credits: 50,
    price: 758.40,
    billingPeriod: 'yearly',
    monthlyEquivalent: 63.20,
    features: [
      '50 credits/month',
      'PlanCraft Strategy Engine',
      'Editorial Calendar',
      'Priority Support',
    ],
    badge: 'SAVE 20%',
  },
  {
    id: 'agency_yearly',
    name: 'Agency',
    credits: 150,
    price: 1910.40,
    billingPeriod: 'yearly',
    monthlyEquivalent: 159.20,
    features: [
      '150 credits/month',
      'Unlimited Workspaces',
      'Team Collaboration',
      'Dedicated Account Manager',
    ],
    badge: 'FOR TEAMS',
  },
];

interface CreditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCredits: number;
  requiredCredits: number;
  serviceName: string;
  onPurchaseComplete?: () => void;
}

export const CreditPurchaseModal: React.FC<CreditPurchaseModalProps> = ({
  isOpen,
  onClose,
  currentCredits,
  requiredCredits,
  serviceName,
  onPurchaseComplete: _onPurchaseComplete,
}) => {
  const { getAuthToken } = useAuth();
  const { paymentsEnabled, loading: paymentsLoading } = usePaymentsEnabled();
  const [selectedPack, setSelectedPack] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const creditsNeeded = Math.max(0, requiredCredits - currentCredits);
  const isNative = Platform.isNative();

  // Find best pack recommendation
  const recommendedPack = CREDIT_PACKS.find(
    (pack) => pack.credits + pack.bonusCredits >= creditsNeeded
  ) || CREDIT_PACKS[CREDIT_PACKS.length - 1];

  // Explain a closed door. Deliberately after `paymentsLoading`, so the
  // message never flashes while the check is still in flight.
  useEffect(() => {
    if (!paymentsLoading && !paymentsEnabled) {
      setError(PAYMENTS_UNAVAILABLE_MESSAGE);
    }
  }, [paymentsLoading, paymentsEnabled]);

  const handlePurchase = async (packId: string) => {
    // Backend-sourced payments switch; see hooks/usePaymentsEnabled.
    if (!paymentsEnabled) return;
    setIsLoading(true);
    setError(null);

    if (isNative) {
      // On native apps, redirect to web checkout
      const checkoutUrl = `https://contentsage.app/checkout?pack=${packId}`;
      openPurchasePage(checkoutUrl);
      setIsLoading(false);
      onClose();
    } else {
      // On web, create checkout session via API
      try {
        const token = await getAuthToken();
        if (!token) {
          setError('Please log in to purchase credits');
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE}/api/v1/payments/create-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ plan_type: packId }),
        });

        if (response.ok) {
          const data = await response.json();
          window.location.href = data.checkout_url;
        } else if (await wasBlockedByKillSwitch(response)) {
          // detail is an OBJECT for this case; passing it to setError as-is
          // would put {reason, message} into React state and throw
          // "Objects are not valid as a React child" on the next render.
          setError(
            'Purchases are paused right now. Your existing plan and credits are ' +
              'unaffected - please try again shortly.',
          );
        } else {
          const errorData = await response.json().catch(() => ({}));
          const detail = errorData.detail;
          setError(
            typeof detail === 'string'
              ? detail
              : 'Failed to create checkout session',
          );
        }
      } catch (err) {
        console.error('Failed to create checkout:', err);
        setError('Network error. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubscribe = () => {
    const planId = selectedPack?.includes('monthly') ? selectedPack : 'pro_monthly';
    if (isNative) {
      openPurchasePage(`https://contentsage.app/checkout?plan=${planId}`);
      onClose();
    } else {
      handlePurchase(planId);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end justify-center z-50"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-h-[90vh] bg-white rounded-t-3xl overflow-hidden pb-[env(safe-area-inset-bottom)]"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-xl">
                <Zap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Unlock {serviceName}</h2>
                <p className="text-sm text-gray-500">
                  You need {creditsNeeded} more credit{creditsNeeded !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="overflow-y-auto max-h-[calc(90vh-100px)] px-6 py-4">
            {/* Current Balance */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-200 rounded-lg">
                  <CreditCard className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current Balance</p>
                  <p className="text-lg font-bold text-gray-900">{currentCredits} Credits</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Required</p>
                <p className="text-lg font-bold text-red-500">{requiredCredits} Credits</p>
              </div>
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setShowSubscription(false)}
                className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors ${
                  !showSubscription
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Credit Packs
              </button>
              <button
                onClick={() => setShowSubscription(true)}
                className={`flex-1 py-2 px-4 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                  showSubscription
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                <Crown className="w-4 h-4" />
                Subscribe & Save
              </button>
            </div>

            <AnimatePresence mode="wait">
              {!showSubscription ? (
                <motion.div
                  key="packs"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-3"
                >
                  {CREDIT_PACKS.map((pack) => {
                    const isRecommended = pack.id === recommendedPack.id;
                    const totalCredits = pack.credits + pack.bonusCredits;
                    const pricePerCredit = (pack.price / totalCredits).toFixed(2);

                    return (
                      <motion.button
                        key={pack.id}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedPack(pack.id)}
                        className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                          selectedPack === pack.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : isRecommended
                            ? 'border-amber-300 bg-amber-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                selectedPack === pack.id
                                  ? 'bg-indigo-500 text-white'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              <Zap className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-gray-900">
                                  {pack.name} Pack
                                </span>
                                {pack.badge && (
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400 text-amber-900">
                                    {pack.badge}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>{pack.credits} credits</span>
                                {pack.bonusCredits > 0 && (
                                  <span className="flex items-center gap-1 text-emerald-600">
                                    <Gift className="w-3 h-3" />
                                    +{pack.bonusCredits} bonus
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900">${pack.price}</p>
                            <p className="text-xs text-gray-400">${pricePerCredit}/credit</p>
                          </div>
                        </div>

                        {isRecommended && selectedPack !== pack.id && (
                          <div className="mt-3 pt-3 border-t border-amber-200 flex items-center gap-2 text-sm text-amber-700">
                            <Sparkles className="w-4 h-4" />
                            <span>Recommended for this purchase</span>
                          </div>
                        )}
                      </motion.button>
                    );
                  })}

                  {/* Purchase Button */}
                  <button
                    onClick={() => selectedPack && handlePurchase(selectedPack)}
                    disabled={!selectedPack || isLoading || !paymentsEnabled}
                    className={`w-full py-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors ${
                      selectedPack && !isLoading
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        {isNative ? (
                          <>
                            <ExternalLink className="w-5 h-5" />
                            Open Checkout
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5" />
                            Purchase Credits
                          </>
                        )}
                      </>
                    )}
                  </button>

                  {error && (
                    <p className="text-xs text-center text-red-500 mb-2">
                      {error}
                    </p>
                  )}

                  {isNative && (
                    <p className="text-xs text-center text-gray-400">
                      You'll be redirected to our secure web checkout
                    </p>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="subscription"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-3"
                >
                  {/* Subscription Plans */}
                  {SUBSCRIPTION_PLANS.map((plan) => {
                    const isSelected = selectedPack === plan.id;
                    const gradients: Record<string, string> = {
                      'starter_monthly': 'from-emerald-500 to-teal-600',
                      'pro_monthly': 'from-indigo-500 to-purple-600',
                      'agency_monthly': 'from-amber-500 to-orange-600',
                    };

                    return (
                      <div
                        key={plan.id}
                        onClick={() => setSelectedPack(plan.id)}
                        className={`bg-gradient-to-br ${gradients[plan.id]} rounded-2xl p-4 text-white cursor-pointer transition-all ${
                          isSelected ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900 scale-[1.02]' : 'opacity-80 hover:opacity-100'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Crown className="w-5 h-5" />
                            <span className="font-bold">{plan.name}</span>
                          </div>
                          {plan.badge && (
                            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                              {plan.badge}
                            </span>
                          )}
                        </div>

                        <div className="flex items-end gap-1 mb-2">
                          <span className="text-2xl font-bold">${plan.price.toFixed(2)}</span>
                          <span className="text-white/70 text-sm mb-0.5">/year</span>
                        </div>

                        <div className="flex flex-col gap-1 text-sm">
                          <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4 text-emerald-300" />
                            <span className="text-emerald-300 font-medium">
                              ${plan.monthlyEquivalent?.toFixed(2)}/mo · Save 20%
                            </span>
                          </div>
                          <span className="text-white/90 ml-6">
                            <strong>{plan.credits} credits</strong>/month
                          </span>
                        </div>

                        {isSelected && (
                          <ul className="mt-3 pt-3 border-t border-white/20 space-y-1">
                            {plan.features.map((feature, i) => (
                              <li key={i} className="flex items-center gap-2 text-xs">
                                <Check className="w-3 h-3 text-emerald-300" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}

                  {/* Subscribe Button */}
                  <button
                    onClick={handleSubscribe}
                    disabled={isLoading || !selectedPack?.includes('monthly') || !paymentsEnabled}
                    className="w-full py-4 bg-indigo-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Crown className="w-5 h-5" />
                        Subscribe Now
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-center text-gray-400">
                    Cancel anytime. Credits reset monthly.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreditPurchaseModal;
